import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Input, Label, Switch } from "@cieda/djayma-ui";
import { AdminForm } from "@/components/admin/shared/AdminForm";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { MarketingImageEditor } from "@/components/admin/images";
import {
  productFormSchema,
  type ProductFormValues,
  type Product,
  BRAND_OPTIONS,
  CATEGORY_OPTIONS,
  TVA_OPTIONS,
  UNIT_OPTIONS,
  computePriceTTC,
  suggestSKU,
  productToFormValues,
  formToApiPayload,
} from "@/lib/validation/product";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ProductFormProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly product?: Product | null;
  readonly onSubmit: (data: Record<string, unknown>) => void | Promise<void>;
  readonly isSubmitting?: boolean;
}

// ---------------------------------------------------------------------------
// FCFA formatter (display only)
// ---------------------------------------------------------------------------

const FCFA = new Intl.NumberFormat("fr-SN", {
  style: "decimal",
  maximumFractionDigits: 0,
});

// ---------------------------------------------------------------------------
// Inner form fields — renders inside FormProvider context
// ---------------------------------------------------------------------------

function ProductFormFields({ isEditing }: { isEditing: boolean }) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<ProductFormValues>();

  const priceHT = watch("price_ht");
  const tvaRate = watch("tva_rate");
  const brand = watch("brand");
  const name = watch("name");

  // Auto-compute TTC price
  const priceTTC = useMemo(
    () => (priceHT > 0 ? computePriceTTC(priceHT, tvaRate) : 0),
    [priceHT, tvaRate],
  );

  // Auto-suggest SKU for new products when brand + name change
  useEffect(() => {
    if (isEditing) return;
    if (brand && name && name.length >= 2) {
      setValue("sku", suggestSKU(brand, name), { shouldValidate: false });
    }
  }, [brand, name, isEditing, setValue]);

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {/* Name */}
      <div className="sm:col-span-2">
        <Label htmlFor="name">Nom du produit *</Label>
        <Input
          id="name"
          {...register("name")}
          placeholder="Ex: Mayonnaise Yousra 500ml"
          className="mt-1"
        />
        {errors.name && (
          <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>
        )}
      </div>

      {/* Wolof name */}
      <div className="sm:col-span-2">
        <Label htmlFor="name_wo">Nom en Wolof (optionnel)</Label>
        <Input
          id="name_wo"
          {...register("name_wo")}
          placeholder="Tur wu Wolof"
          className="mt-1"
        />
      </div>

      {/* SKU */}
      <div>
        <Label htmlFor="sku">SKU *</Label>
        <Input
          id="sku"
          {...register("sku")}
          placeholder="YSR-MAYO-123"
          className="mt-1 font-mono text-sm"
        />
        {errors.sku && (
          <p className="mt-1 text-xs text-red-600">{errors.sku.message}</p>
        )}
      </div>

      {/* Brand */}
      <div>
        <Label htmlFor="brand">Marque *</Label>
        <select
          id="brand"
          {...register("brand")}
          className="mt-1 h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
        >
          <option value="">Sélectionner une marque</option>
          {BRAND_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {errors.brand && (
          <p className="mt-1 text-xs text-red-600">{errors.brand.message}</p>
        )}
      </div>

      {/* Category */}
      <div>
        <Label htmlFor="category">Catégorie *</Label>
        <select
          id="category"
          {...register("category")}
          className="mt-1 h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
        >
          <option value="">Sélectionner une catégorie</option>
          {CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {errors.category && (
          <p className="mt-1 text-xs text-red-600">{errors.category.message}</p>
        )}
      </div>

      {/* Price HT */}
      <div>
        <Label htmlFor="price_ht">Prix HT (FCFA) *</Label>
        <Input
          id="price_ht"
          type="number"
          {...register("price_ht", { valueAsNumber: true })}
          placeholder="1 000"
          className="mt-1"
          min={1}
        />
        {errors.price_ht && (
          <p className="mt-1 text-xs text-red-600">{errors.price_ht.message}</p>
        )}
      </div>

      {/* TVA rate */}
      <div>
        <Label htmlFor="tva_rate">Taux de TVA</Label>
        <select
          id="tva_rate"
          {...register("tva_rate", { valueAsNumber: true })}
          className="mt-1 h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
        >
          {TVA_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Price TTC (read-only) */}
      <div className="sm:col-span-2">
        <div className="rounded-md border border-stone-100 bg-stone-50 px-4 py-3">
          <span className="text-sm text-stone-500">Prix TTC : </span>
          <span className="font-semibold text-stone-900">
            {priceTTC > 0 ? `${FCFA.format(priceTTC)} FCFA` : "—"}
          </span>
        </div>
      </div>

      {/* Weight */}
      <div>
        <Label htmlFor="weight_kg">Poids (kg) *</Label>
        <Input
          id="weight_kg"
          type="number"
          step="0.01"
          {...register("weight_kg", { valueAsNumber: true })}
          placeholder="0.5"
          className="mt-1"
          min={0.01}
        />
        {errors.weight_kg && (
          <p className="mt-1 text-xs text-red-600">{errors.weight_kg.message}</p>
        )}
      </div>

      {/* Unit */}
      <div>
        <Label htmlFor="unit">Unité *</Label>
        <select
          id="unit"
          {...register("unit")}
          className="mt-1 h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
        >
          <option value="">Sélectionner</option>
          {UNIT_OPTIONS.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
        {errors.unit && (
          <p className="mt-1 text-xs text-red-600">{errors.unit.message}</p>
        )}
      </div>

      {/* Min order qty */}
      <div>
        <Label htmlFor="min_order_qty">Quantité min. de commande</Label>
        <Input
          id="min_order_qty"
          type="number"
          {...register("min_order_qty", { valueAsNumber: true })}
          placeholder="1"
          className="mt-1"
          min={1}
        />
        {errors.min_order_qty && (
          <p className="mt-1 text-xs text-red-600">{errors.min_order_qty.message}</p>
        )}
      </div>

      {/* Image upload */}
      <div className="sm:col-span-2">
        <MarketingImageEditor
          preset="product"
          value={watch("image_url") || undefined}
          onChange={(url) => setValue("image_url", url)}
          label="Image du produit (optionnel)"
        />
      </div>

      {/* Description */}
      <div className="sm:col-span-2">
        <Label htmlFor="description">Description</Label>
        <textarea
          id="description"
          {...register("description")}
          placeholder="Description du produit..."
          rows={3}
          className="mt-1 w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
        />
      </div>

      {/* Toggle: Active */}
      <div className="flex items-center justify-between rounded-md border border-stone-200 px-4 py-3">
        <div>
          <p className="text-sm font-medium text-stone-900">Actif</p>
          <p className="text-xs text-stone-500">Visible sur la boutique</p>
        </div>
        <Switch
          checked={watch("active")}
          onCheckedChange={(checked: boolean) => setValue("active", checked)}
        />
      </div>

      {/* Toggle: Featured */}
      <div className="flex items-center justify-between rounded-md border border-stone-200 px-4 py-3">
        <div>
          <p className="text-sm font-medium text-stone-900">Produit vedette</p>
          <p className="text-xs text-stone-500">Affiché en page d'accueil</p>
        </div>
        <Switch
          checked={watch("is_featured")}
          onCheckedChange={(checked: boolean) => setValue("is_featured", checked)}
        />
      </div>

      {/* Toggle: CIEDA Brand */}
      <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50/50 px-4 py-3 sm:col-span-2">
        <div>
          <p className="text-sm font-medium text-stone-900">Produit CIEDA</p>
          <p className="text-xs text-stone-500">
            Affiché sur ciedagroup.com (vitrine corporate)
          </p>
        </div>
        <Switch
          checked={watch("is_cieda_brand")}
          onCheckedChange={(checked: boolean) => setValue("is_cieda_brand", checked)}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ProductForm({
  open,
  onClose,
  product,
  onSubmit,
}: ProductFormProps) {
  const isEditing = !!product;
  const title = isEditing ? "Modifier le produit" : "Nouveau produit";

  const defaultValues: ProductFormValues = useMemo(() => {
    if (product) return productToFormValues(product);
    return {
      name: "",
      name_wo: "",
      sku: "",
      brand: "" as ProductFormValues["brand"],
      category: "" as ProductFormValues["category"],
      price_ht: 0,
      tva_rate: 0.18,
      weight_kg: 0,
      unit: "",
      min_order_qty: 1,
      description: "",
      active: true,
      is_featured: false,
      is_cieda_brand: false,
      image_url: "",
    };
  }, [product]);

  const handleSubmit = async (values: ProductFormValues) => {
    const payload = formToApiPayload(values);
    await onSubmit(payload);
    onClose();
  };

  return (
    <AdminModal open={open} onClose={onClose} title={title} size="lg">
      <div className="max-h-[70vh] overflow-y-auto">
        <AdminForm<ProductFormValues>
          key={product?.id ?? "new"}
          schema={productFormSchema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          submitLabel={isEditing ? "Mettre à jour" : "Créer le produit"}
        >
          <ProductFormFields isEditing={isEditing} />
        </AdminForm>
      </div>
    </AdminModal>
  );
}
